<template>
  <div class="app-detail-page">
    <PageHeader
      :actions="headerActions"
      @action="onHeaderAction"
    >
      <template #left>
        <div class="app-detail-header">
          <AppLogoBadge
            :src="supplierLogo"
            :alt="`Logo de ${supplierName}`"
            :initials="supplierInitials"
            variant="detail"
            aria-hidden="true"
          />

          <div class="app-detail-heading">
            <h2>{{ supplierName }}</h2>
            <div class="app-detail-heading-meta">
              <span
                class="app-status-pill app-detail-status-pill"
                :class="supplierIsActive ? 'app-status-active' : 'app-status-inactive'"
              >
                {{ supplierStatusLabel }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <template #actions="{ onAction }">
        <div class="app-page-header-actions app-detail-header-actions">
          <button
            v-for="action in primaryHeaderActions"
            :key="action.key"
            class="app-header-btn"
            :class="`app-header-btn--${action.variant || 'primary'}`"
            type="button"
            @click="handlePrimaryHeaderAction(action, onAction)"
          >
            <i v-if="action.icon" class="fa" :class="action.icon" aria-hidden="true"></i>
            <span>{{ action.label }}</span>
          </button>

          <div v-if="secondaryHeaderActions.length" class="app-detail-more-actions-wrap">
            <button
              type="button"
              class="app-header-btn app-header-btn--ghost app-detail-more-actions-btn"
              :title="text.detail.header.moreActions"
              @click.stop="toggleMoreActionsMenu"
            >
              <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
            </button>

            <div v-if="showMoreActionsMenu" class="app-detail-more-actions-menu">
              <button
                v-for="action in secondaryHeaderActions"
                :key="action.key"
                type="button"
                @click="handleSecondaryHeaderAction(action, onAction)"
              >
                {{ action.label }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </PageHeader>

    <div class="app-detail-content">
      <section v-if="loading" class="app-detail-loading card">
        <div class="card-body">{{ text.detail.loading.detail }}</div>
      </section>

      <template v-else>
        <section class="metrics-grid app-detail-kpis">
          <CustomersKpiCard
            v-for="metric in detailMetrics"
            :key="metric.key"
            :title="metric.title"
            :value="metric.value"
            :value-meta="metric.valueMeta"
            :value-scale="metric.valueScale"
            :icon="metric.icon"
            :icon-bg="metric.iconBg"
            :icon-color="metric.iconColor"
          />
        </section>

        <AppSectionTabs
          v-model="activeTab"
          :tabs="tabs"
          :aria-label="text.detail.header.tabsAriaLabel"
        />

        <SupplierDetailTabsPanel
          :active-tab="activeTab"
          :tabs="tabs"
          :supplier="supplier"
          :supplier-overview="supplierOverview"
          :persons="persons"
          :default-billing-data="defaultBillingData"
          :expense-rows="paginatedExpenseRows"
          :expense-loading="expenseLoading"
          :expense-filter-options="expenseFilterOptions"
          :expense-pagination="expensePagination"
          :expense-status-tabs="expenseStatusTabs"
          :expense-active-status-tab="expenseActiveStatusTab"
          :expense-summary="expenseSummary"
          :fee-rows="paginatedFeeRows"
          :fee-loading="feeLoading"
          :fee-filter-options="feeFilterOptions"
          :fee-pagination="feePagination"
          :fee-status-tabs="feeStatusTabs"
          :fee-active-status-tab="feeActiveStatusTab"
          :fee-summary="feeSummary"
          @edit-section="handleEditSection"
          @add-contact="openPersonCreateModal"
          @edit-contact="openPersonEditModal"
          @delete-contact="handleDeleteContact"
          @edit-billing-data="handleEditBillingData"
          @open-billing-data-list="handleOpenBillingDataList"
          @create-billing-data="handleCreateBillingData"
          @expenses-search-change="handleSupplierExpensesSearchChange"
          @expenses-filters-change="handleSupplierExpensesFiltersChange"
          @expenses-status-tab-select="handleSupplierExpensesStatusTabSelect"
          @expenses-page-change="handleSupplierExpensesPageChange"
          @pay-expense="handleSupplierPayExpense"
          @edit-expense="handleSupplierEditExpense"
          @delete-expense="handleSupplierDeleteExpense"
          @approve-expense="handleSupplierApproveExpense"
          @download-xml="handleSupplierDownloadExpenseXml"
          @download-pdf="handleSupplierDownloadExpensePdf"
          @download-receipt="handleSupplierDownloadExpenseReceipt"
          @fees-search-change="handleSupplierFeesSearchChange"
          @fees-filters-change="handleSupplierFeesFiltersChange"
          @fees-status-tab-select="handleSupplierFeesStatusTabSelect"
          @fees-page-change="handleSupplierFeesPageChange"
          @pay-fee="handleSupplierPayFee"
          @edit-fee="handleSupplierEditFee"
          @delete-fee="handleSupplierDeleteFee"
          @approve-fee="handleSupplierApproveFee"
          @download-fee-xml="handleSupplierDownloadFeeXml"
          @download-fee-pdf="handleSupplierDownloadFeePdf"
          @download-fee-receipt="handleSupplierDownloadFeeReceipt"
          @new-expense-from-supplier="handleSupplierNewExpenseFromOperation"
          @new-fee-from-supplier="handleSupplierNewFeeFromOperation"
          @open-merge="openSupplierMergeModal"
          @archive-toggle="handleArchiveSupplierToggle"
          @delete-supplier="handleDeleteSupplierFromAdvancedOptions"
          @open-history="openHistoryTab"
        />
      </template>
    </div>

    <SupplierModal
      :show="isSupplierEditModalOpen"
      mode="edit"
      :initial-data="supplier"
      :initial-section-key="supplierEditSectionKey"
      @close="closeSupplierEditModal"
      @submit="submitSupplierEdit"
      @delete="handleDeleteSupplierFromModal"
    />

    <SupplierMergeModal
      :show="isMergeModalOpen"
      :primary-supplier="supplier"
      :submitting="mergeSubmitting"
      @close="closeSupplierMergeModal"
      @submit="submitSupplierMerge"
    />

    <ConfirmActionModal
      :show="isConfirmActionModalOpen"
      :title="confirmActionTitle"
      :subtitle="confirmActionSubtitle"
      :message="confirmActionMessage"
      :confirm-text="confirmActionButton"
      :cancel-text="text.actions.common.cancel"
      @cancel="closeConfirmActionModal"
      @confirm="confirmPendingAction"
    />

    <ContributorModal
      :show="isContributorModalOpen"
      :mode="contributorModalMode"
      :initial-data="contributorModalInitialData"
      :state-options="stateOptions"
      :regimen-options="regimenOptions"
      :uso-cfdi-options="usoCfdiOptions"
      :payment-method-options="paymentMethodOptions"
      :payment-method-type-options="paymentMethodTypeOptions"
      :show-delete-action="true"
      :loading="isContributorSubmitting"
      @close="closeContributorModal"
      @delete="handleDeleteContributorFromModal"
      @submit="submitContributor"
    />

    <ContributorListModal
      :show="isContributorListModalOpen"
      :contributors="contributors"
      @close="closeContributorListModal"
      @edit-contributor="handleEditContributorFromList"
      @delete-contributor="handleDeleteContributor"
    />

    <PersonModal
      :show="isPersonModalOpen"
      :mode="personModalMode"
      :initial-data="personModalInitialData"
      :country-options="countryOptions"
      :state-options="stateOptions"
      :loading="isPersonSubmitting"
      @close="closePersonModal"
      @delete="handleDeleteContactFromModal"
      @submit="submitPerson"
    />

    <ExpenseModal
      :show="isExpenseModalOpen"
      :mode="expenseModalMode"
      :initial-data="expenseModalInitialData"
      :submitting="isExpenseSubmitting"
      @close="closeExpenseModal"
      @submit="submitExpense"
    />

    <ConfirmActionModal
      :show="isApproveExpenseModalOpen"
      :title="expenseApproveModal.title"
      :subtitle="expenseApproveModal.subtitle"
      :message="expenseApproveModal.message"
      :confirm-text="expenseApproveModal.confirmText"
      :cancel-text="expenseApproveModal.cancelText"
      @cancel="closeSupplierApproveExpenseModal"
      @confirm="confirmSupplierApproveExpense"
    />

    <ConfirmActionModal
      :show="isDeleteExpenseModalOpen"
      :title="expenseDeleteModal.title"
      :subtitle="expenseDeleteModal.subtitle"
      :message="expenseDeleteModal.message"
      :confirm-text="expenseDeleteModal.confirmText"
      :cancel-text="expenseDeleteModal.cancelText"
      @cancel="closeSupplierDeleteExpenseModal"
      @confirm="confirmSupplierDeleteExpense"
    />

    <PayModal
      :show="isPayExpenseModalOpen"
      :row="payExpenseTarget"
      context="expense-fee"
      entity-type="expense"
      :submitting="isPayExpenseSubmitting"
      @close="closeSupplierPayExpenseModal"
      @submit="submitSupplierPayExpense"
    />

    <FeeModal
      :show="isFeeModalOpen"
      :mode="feeModalMode"
      :initial-data="feeModalInitialData"
      :submitting="isFeeSubmitting"
      @close="closeFeeModal"
      @submit="submitFee"
    />

    <ConfirmActionModal
      :show="isApproveFeeModalOpen"
      :title="feeApproveModal.title"
      :subtitle="feeApproveModal.subtitle"
      :message="feeApproveModal.message"
      :confirm-text="feeApproveModal.confirmText"
      :cancel-text="feeApproveModal.cancelText"
      @cancel="closeSupplierApproveFeeModal"
      @confirm="confirmSupplierApproveFee"
    />

    <ConfirmActionModal
      :show="isDeleteFeeModalOpen"
      :title="feeDeleteModal.title"
      :subtitle="feeDeleteModal.subtitle"
      :message="feeDeleteModal.message"
      :confirm-text="feeDeleteModal.confirmText"
      :cancel-text="feeDeleteModal.cancelText"
      @cancel="closeSupplierDeleteFeeModal"
      @confirm="confirmSupplierDeleteFee"
    />

    <PayModal
      :show="isPayFeeModalOpen"
      :row="payFeeTarget"
      context="expense-fee"
      entity-type="fee"
      :submitting="isPayFeeSubmitting"
      @close="closeSupplierPayFeeModal"
      @submit="submitSupplierPayFee"
    />
  </div>
</template>

<script>
import AppLogoBadge from '@/components/common/AppLogoBadge.vue';
import PageHeader from '@/components/common/PageHeader.vue';
import AppSectionTabs from '@/components/common/AppSectionTabs.vue';
import CustomersKpiCard from '@/components/customers/CustomersKpiCard.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import ExpenseModal from '@/components/expenses/ExpenseModal.vue';
import FeeModal from '@/components/fees/FeeModal.vue';
import PayModal from '@/components/pay/modal/PayModal.vue';
import PersonModal from '@/components/people/PersonModal.vue';
import ContributorModal from '@/components/contributors/ContributorModal.vue';
import ContributorListModal from '@/components/contributors/ContributorListModal.vue';
import SupplierModal from '@/components/suppliers/SupplierModal.vue';
import SupplierDetailTabsPanel from '@/components/suppliers/detail/SupplierDetailTabsPanel.vue';
import SupplierMergeModal from '@/components/suppliers/detail/modals/SupplierMergeModal.vue';
import { personDetailMethods } from '@/composables/people/personDetailMethods';
import { contributorMethods } from '@/composables/contributors/contributorMethods';
import {
  createSupplierDetailState,
  supplierDetailComputed,
  supplierDetailMethods,
} from '@/composables/suppliers/useSupplierDetailPage';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('suppliers');

export default {
  name: 'SupplierDetailPage',
  components: {
    AppLogoBadge,
    PageHeader,
    AppSectionTabs,
    CustomersKpiCard,
    ConfirmActionModal,
    ExpenseModal,
    FeeModal,
    PayModal,
    PersonModal,
    ContributorModal,
    ContributorListModal,
    SupplierModal,
    SupplierDetailTabsPanel,
    SupplierMergeModal,
  },
  data() {
    return {
      text,
      ...createSupplierDetailState(text),
    };
  },
  computed: {
    ...supplierDetailComputed,
  },
  mounted() {
    this.fetchSupplierDetail();
    document.addEventListener('click', this.handleHeaderDocumentClick);
  },
  beforeUnmount() {
    this.clearExpenseSearchDebounce();
    this.clearFeeSearchDebounce();
    document.removeEventListener('click', this.handleHeaderDocumentClick);
  },
  watch: {
    supplierId(nextId, previousId) {
      if (nextId !== previousId) {
        this.fetchSupplierDetail();
      }
    },
    activeTab(nextTab, previousTab) {
      if (nextTab !== previousTab && nextTab === 'operation') {
        this.fetchSupplierExpenses();
        this.fetchSupplierFees();
      }
    },
  },
  methods: {
    ...personDetailMethods,
    ...contributorMethods,
    ...supplierDetailMethods,
  },
};
</script>
